/**
 * @file EnterprisePipelineReport.tsx
 * @brief Super Admin report — Enterprise inquiries + seat inventory.
 *
 * @author DES
 * @date 27-07-2026
 */

import { useMemo, useState } from "react";
import { Briefcase, Building2, Users } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { StatCard } from "@/features/analytics/components/StatCard";
import type { AnalyticsRange as RangeBarValue } from "@/features/analytics/components/AnalyticsRangeBar";
import {
  ReportKpiCell,
  ReportKpiRow,
  ReportSectionHeader,
  ReportTableCell,
} from "@/features/reports-shared/ReportSection";
import { formatDate, formatDateTime } from "@/shared/utils/formatters";
import { useEnterprisePipelineReport } from "../api";
import { ReportShell } from "./ReportShell";
import { ReportTable } from "./ReportTable";

function defaultRange(): RangeBarValue {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 90);
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);
  return {
    startDate: start.toISOString(),
    endDate: end.toISOString(),
    groupBy: "week",
  };
}

export function EnterprisePipelineReportView() {
  const [range, setRange] = useState<RangeBarValue>(() => defaultRange());
  const queryRange = useMemo(
    () => ({ startDate: range.startDate, endDate: range.endDate }),
    [range.startDate, range.endDate],
  );
  const report = useEnterprisePipelineReport(queryRange);
  const summary = report.data?.summary;
  const inquiries = report.data?.inquiries ?? [];
  const plans = report.data?.plans ?? [];

  return (
    <ReportShell
      title="Enterprise pipeline"
      description="Inquiries in range plus live Enterprise seat sold vs assigned."
      range={range}
      onRangeChange={setRange}
      exportDisabled={inquiries.length === 0 && plans.length === 0}
      serverExport={{
        scope: "admin",
        reportKey: "enterprise-pipeline",
        params: {
          startDate: range.startDate,
          endDate: range.endDate,
        },
      }}
      enablePdf
    >
      <>
        <ReportKpiRow className="xl:grid-cols-3">
          <ReportKpiCell>
            <StatCard
              variant="cell"
              label="Inquiries"
              icon={Briefcase}
              value={String(summary?.total_inquiries ?? 0)}
              hint={`${summary?.seats_requested ?? 0} seats requested`}
              isLoading={report.isLoading}
            />
          </ReportKpiCell>
          <ReportKpiCell>
            <StatCard
              variant="cell"
              label="Active plans"
              icon={Building2}
              value={String(summary?.active_plans ?? 0)}
              isLoading={report.isLoading}
            />
          </ReportKpiCell>
          <ReportKpiCell>
            <StatCard
              variant="cell"
              label="Seats sold"
              icon={Users}
              value={String(summary?.seats_sold ?? 0)}
              hint={`${summary?.seats_assigned ?? 0} assigned · ${summary?.seats_unassigned ?? 0} open`}
              isLoading={report.isLoading}
            />
          </ReportKpiCell>
        </ReportKpiRow>

        <ReportSectionHeader
          title="Inquiries"
          description="Newest first (up to 300)."
        />
        <ReportTableCell>
          <ReportTable
            headers={[
              "Status",
              "Intent",
              "Company",
              "Contact",
              { label: "Seats", align: "right" },
              { label: "Created", align: "right" },
            ]}
            isLoading={report.isLoading}
            empty={inquiries.length === 0}
          >
            {inquiries.map((row) => (
              <tr key={row.id} className="hover:bg-secondary/20">
                <td className="px-4 py-2">
                  <Badge variant="outline">{row.status}</Badge>
                </td>
                <td className="px-4 py-2 capitalize text-muted-foreground">
                  {row.intent === "change_plan" ? "Change / end" : "New deal"}
                </td>
                <td className="px-4 py-2">{row.company_name || "—"}</td>
                <td className="px-4 py-2">
                  <div className="font-medium">{row.contact_name}</div>
                  <div className="text-xs text-muted-foreground">
                    {row.contact_email}
                  </div>
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {row.seats_required}
                </td>
                <td className="px-4 py-2 text-right text-muted-foreground">
                  {formatDateTime(row.created_at)}
                </td>
              </tr>
            ))}
          </ReportTable>
        </ReportTableCell>

        <ReportSectionHeader
          title="Enterprise plans"
          description="Active and suspended seat inventory."
        />
        <ReportTableCell>
          <ReportTable
            headers={[
              "Company",
              "Status",
              { label: "Seats", align: "right" },
              { label: "Pool minutes", align: "right" },
              { label: "Period", align: "right" },
            ]}
            empty={plans.length === 0}
            emptyMessage="No Enterprise plans."
          >
            {plans.map((row) => (
              <tr key={row.company_id} className="hover:bg-secondary/20">
                <td className="px-4 py-2 font-medium">
                  {row.company_name || "—"}
                </td>
                <td className="px-4 py-2 capitalize">{row.status}</td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {row.seats_assigned} / {row.seat_limit}
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {row.included_minutes}
                </td>
                <td className="px-4 py-2 text-right text-muted-foreground">
                  {formatDate(row.period_start)} → {formatDate(row.period_end)}
                </td>
              </tr>
            ))}
          </ReportTable>
        </ReportTableCell>
      </>
    </ReportShell>
  );
}
